import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { TFunction, useTranslation } from 'react-i18next'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { createEarnPortfolio } from 'services/EarnService'
import { BaseErrorResponse } from 'types/beneficiaries'
import { CreatePortfolioRequest, Portfolio, PortfolioStatus } from '../../types'
import { useEarnTracking } from '../useEarnTracking'
import { useEarnPortfolioQuery } from './useEarnPortfolioQuery'

export const getCreatePortfolioTimeoutError = (t: TFunction) =>
  new Error(t('earn.errors.portfolioActivationTimeout'))
export const getCreatePortfolioFailedError = (t: TFunction) =>
  new Error(t('earn.errors.portfolioCreationFailed'))

export const useCreatePortfolio = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: Portfolio) => void
  onError?: (error: BaseErrorResponse) => void
}) => {
  const { t } = useTranslation()
  const { trackSuccess, trackError } = useEarnTracking()
  const queryClient = useQueryClient()

  const [pollingState, setPollingState] = useState<{
    portfolioId: string
    resolver: (portfolio: Portfolio) => void
    rejecter: (error: Error) => void
  } | null>(null)

  const { mutateAsync: createPortfolio, isPending: isCreatingPortfolioPending } = useMutation<
    Portfolio,
    Error,
    CreatePortfolioRequest
  >({ mutationFn: createEarnPortfolio })

  const isPolling = !!pollingState?.portfolioId
  const isPending = isCreatingPortfolioPending || isPolling

  const handleMaxPollReached = () => {
    const error = getCreatePortfolioTimeoutError(t)
    trackError('createPortfolio', error, {
      portfolioId: pollingState?.portfolioId,
    })
    queryClient.invalidateQueries({ queryKey: reactQueryKeys.earnDashboard() })
    onError?.(error)
    pollingState?.rejecter(error)
    setPollingState(null)
  }

  const handlePortfolioCreatedFailed = () => {
    const error = getCreatePortfolioFailedError(t)
    trackError('createPortfolio', error, {
      portfolioId: pollingState?.portfolioId,
    })
    queryClient.invalidateQueries({ queryKey: reactQueryKeys.earnDashboard() })
    onError?.(error)
    pollingState?.rejecter(error)
    setPollingState(null)
  }

  const { data: polledPortfolio } = useEarnPortfolioQuery({
    portfolioId: pollingState?.portfolioId || '',
    enabled: !!pollingState?.portfolioId,
    pollUntilActive: true,
    onMaxPollReached: handleMaxPollReached,
    onPortfolioCreatedFailed: handlePortfolioCreatedFailed,
  })

  useEffect(() => {
    if (polledPortfolio?.status === PortfolioStatus.ACTIVE && pollingState) {
      trackSuccess('createPortfolio', { currency: polledPortfolio.currency })
      onSuccess?.(polledPortfolio)
      pollingState.resolver(polledPortfolio)
      setPollingState(null)
    }
  }, [polledPortfolio, pollingState, trackSuccess, onSuccess])

  const handleCreatePortfolio = async (earningProductId: string, reference?: string) => {
    if (pollingState) {
      throw new Error('Portfolio creation already in progress')
    }

    try {
      const createdPortfolio = await createPortfolio({ earningProductId, reference })

      return new Promise<Portfolio>((resolve, reject) => {
        setPollingState({
          portfolioId: createdPortfolio.id,
          resolver: resolve,
          rejecter: reject,
        })
      })
    } catch (error) {
      const baseError = error as BaseErrorResponse
      trackError('createPortfolio', baseError, { productId: earningProductId })
      onError?.(baseError)
      throw error
    }
  }

  return {
    createPortfolio: handleCreatePortfolio,
    isCreatingPortfolioPending: isPending,
  }
}
